import { useMemo, useState } from "react";
import { createFileRoute, useNavigate, Navigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { Bell, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { WorkHoursTimePicker } from "@/components/ui/work-hours-time-picker";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { useActiveProfiles } from "@/hooks/use-admin-data";
import { type ModeratorPermissions } from "@/lib/moderator-permissions";
import { sendPushNotification } from "@/lib/rpc/notifications.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/notifications/send")({
  component: SendNotificationPage,
});

const DEPTS: Department[] = [
  "editor",
  "graphic_designer",
  "ai",
  "scriptwriter",
  "marketer",
  "seo",
  "smm",
  "calling",
  "hr",
  "admin",
];

type SendType = "instant" | "scheduled";
type TargetType = "all" | "department" | "individual";

function SendNotificationPage() {
  const { profile, role, loading } = useAuth();
  const navigate = useNavigate();
  const { data: profiles = [] } = useActiveProfiles();

  const canSend = useMemo(() => {
    if (!profile) return false;
    if (role === "admin") return true;
    if (profile.department === "hr") return true;
    if (role === "moderator") {
      const perms = (profile.moderator_permissions ?? {}) as ModeratorPermissions;
      return Boolean(perms.send_notifications);
    }
    return false;
  }, [profile, role]);

  const [sendType, setSendType] = useState<SendType>("instant");
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [heading, setHeading] = useState("");
  const [description, setDescription] = useState("");
  const [targetType, setTargetType] = useState<TargetType>("all");
  const [targetDepts, setTargetDepts] = useState<Department[]>([]);
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mut = useMutation({
    mutationFn: async (kind: "send" | "draft") => {
      let scheduled_at: string | null = null;
      if (sendType === "scheduled") {
        if (!scheduledDate || !scheduledTime) {
          throw new Error("Pick a date and time");
        }
        const [hh, mm] = scheduledTime.split(":").map(Number);
        const d = new Date(scheduledDate);
        d.setHours(hh, mm, 0, 0);
        if (d.getTime() <= Date.now()) throw new Error("Scheduled time must be in the future");
        scheduled_at = d.toISOString();
      }
      return sendPushNotification({
        data: {
          heading: heading.trim(),
          description: description.trim(),
          send_type: kind === "draft" ? "draft" : sendType,
          scheduled_at,
          target_type: targetType,
          target_dept: targetType === "department" ? targetDepts : null,
          target_user_id: targetType === "individual" ? targetUserId : null,
        },
      });
    },
    onSuccess: (_data, kind) => {
      toast.success(
        kind === "draft"
          ? "Saved as draft"
          : sendType === "instant"
            ? "Notification sent"
            : "Notification scheduled",
      );
      navigate({ to: "/notifications/history" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return null;
  if (!canSend) return <Navigate to="/dashboard" />;

  const audienceCount = useMemo(() => {
    if (targetType === "all") return profiles.length;
    if (targetType === "department")
      return profiles.filter((p) => targetDepts.includes(p.department)).length;
    if (targetType === "individual") return targetUserId ? 1 : 0;
    return 0;
  }, [targetType, targetDepts, targetUserId, profiles]);

  const audienceLabel =
    targetType === "all"
      ? `${audienceCount} employees`
      : targetType === "department"
        ? `${audienceCount} employees in ${targetDepts.length} department${targetDepts.length === 1 ? "" : "s"}`
        : profiles.find((p) => p.id === targetUserId)?.full_name ?? "—";

  const headingValid = heading.trim().length >= 3;
  const descriptionValid = description.trim().length >= 10;
  const targetValid =
    targetType === "all" ||
    (targetType === "department" && targetDepts.length > 0) ||
    (targetType === "individual" && !!targetUserId);
  const scheduleValid =
    sendType === "instant" || (Boolean(scheduledDate) && Boolean(scheduledTime));
  const formValid = headingValid && descriptionValid && targetValid && scheduleValid;

  const onSubmit = () => {
    if (!formValid) {
      toast.error("Please fix validation errors first");
      return;
    }
    setConfirmOpen(true);
  };

  return (
    <div className="flex flex-col">
      <AppHeader subtitle="Push a message to your team instantly or schedule it" />
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <h2 className="text-[18px] font-bold leading-tight">Send Notification</h2>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Push a message to your team instantly or schedule it
        </p>

        <div className="mt-6 grid gap-5">
          {/* SEND TYPE */}
          <Field label="Send Type" required>
            <PillToggle
              value={sendType}
              options={[
                { value: "instant", label: "Instant" },
                { value: "scheduled", label: "Scheduled" },
              ]}
              onChange={(v) => setSendType(v as SendType)}
            />
            {sendType === "scheduled" && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-[11px] font-medium">Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "mt-1 w-full justify-start text-left font-normal",
                          !scheduledDate && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {scheduledDate ? format(scheduledDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={scheduledDate}
                        onSelect={setScheduledDate}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <WorkHoursTimePicker
                  label="Time"
                  value={scheduledTime}
                  onChange={setScheduledTime}
                />
              </div>
            )}
          </Field>

          {/* HEADING */}
          <Field label="Heading" required>
            <Input
              value={heading}
              onChange={(e) => setHeading(e.target.value.slice(0, 80))}
              placeholder="e.g. Team meeting at 3 PM today"
              maxLength={80}
            />
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
              <span>{!headingValid && heading.length > 0 ? "Min 3 characters" : ""}</span>
              <span>{heading.length} / 80</span>
            </div>
          </Field>

          {/* DESCRIPTION */}
          <Field label="Description" required>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              placeholder="Write your message here..."
              rows={4}
              maxLength={500}
            />
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
              <span>{!descriptionValid && description.length > 0 ? "Min 10 characters" : ""}</span>
              <span>{description.length} / 500</span>
            </div>
          </Field>

          {/* SEND TO */}
          <Field label="Send To" required>
            <PillToggle
              value={targetType}
              options={[
                { value: "all", label: "All Employees" },
                { value: "department", label: "By Department" },
                { value: "individual", label: "Individual" },
              ]}
              onChange={(v) => {
                setTargetType(v as TargetType);
                setTargetDepts([]);
                setTargetUserId(null);
              }}
            />

            {targetType === "department" && (
              <div className="mt-3">
                <Label className="text-[11px] font-medium">Department(s)</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {DEPTS.map((d) => {
                    const active = targetDepts.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() =>
                          setTargetDepts((prev) =>
                            prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
                          )
                        }
                        className={cn(
                          "rounded-full border px-3 py-1 text-[11px] font-medium transition-colors",
                          active
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background text-muted-foreground hover:bg-muted",
                        )}
                      >
                        {DEPARTMENT_LABELS[d]}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {targetType === "individual" && (
              <div className="mt-3">
                <Label className="text-[11px] font-medium">Employee</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-1 w-full justify-between"
                    >
                      {targetUserId
                        ? profiles.find((p) => p.id === targetUserId)?.full_name ?? "Select…"
                        : "Search employee by name or ID"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[420px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Type a name or employee ID…" />
                      <CommandList>
                        <CommandEmpty>No employees found.</CommandEmpty>
                        <CommandGroup>
                          {profiles.map((p) => (
                            <CommandItem
                              key={p.id}
                              value={`${p.full_name} ${p.email} ${p.id}`}
                              onSelect={() => setTargetUserId(p.id)}
                            >
                              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-[10px] font-bold">
                                {(p.full_name || p.email).charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-2 flex flex-col">
                                <span className="text-[12px] font-medium">{p.full_name}</span>
                                <span className="text-[10px] text-muted-foreground">
                                  {DEPARTMENT_LABELS[p.department]} · #{p.id.slice(0, 6)}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </Field>

          {/* PREVIEW */}
          <div>
            <Label className="text-[11px] font-medium uppercase tracking-[0.4px] text-muted-foreground">
              Preview
            </Label>
            <div className="mt-2 rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Bell className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold">
                    {heading || "Heading text"}
                  </div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground">
                    {description || "Description text"}
                  </div>
                  <div className="mt-1.5 text-[10px] text-muted-foreground">
                    From: {profile?.full_name || "You"} · Just now
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="mt-2 flex items-center justify-between border-t border-border pt-4">
            <button
              type="button"
              onClick={() => mut.mutate("draft")}
              disabled={mut.isPending || !headingValid || !descriptionValid}
              className="text-[12px] font-medium text-muted-foreground underline-offset-4 hover:underline disabled:opacity-40"
            >
              Save as Draft
            </button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate({ to: "/dashboard" })}
                disabled={mut.isPending}
              >
                Cancel
              </Button>
              <Button onClick={onSubmit} disabled={mut.isPending || !formValid}>
                {sendType === "instant" ? "Send Now →" : "Schedule →"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {sendType === "instant" ? "Send notification?" : "Schedule notification?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Send to <span className="font-semibold">{audienceLabel}</span>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => mut.mutate("send")} disabled={mut.isPending}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="text-[11px] font-medium uppercase tracking-[0.4px] text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function PillToggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-full border border-border bg-muted p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-full px-4 py-1.5 text-[11px] font-medium transition-colors",
            value === o.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-background",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
